/** The map legend (docs/spec/03 §1): characters to elements and back. */

import { CDIR_DOWN, CDIR_LEFT, CDIR_RIGHT, CDIR_UP, E, isButterfly, isFirefly } from '../sim/elements';
import type { CaveState } from '../sim/types';

export const LEGEND: Readonly<Record<string, number>> = {
  W: E.STEEL,
  w: E.BRICK,
  m: E.MAGIC_WALL,
  '.': E.DIRT,
  ' ': E.SPACE,
  r: E.BOULDER,
  d: E.DIAMOND,
  P: E.INBOX,
  X: E.OUTBOX_CLOSED,
  a: E.AMOEBA,
  f: E.FIREFLY + CDIR_LEFT,
  F: E.FIREFLY + CDIR_RIGHT,
  b: E.BUTTERFLY + CDIR_DOWN,
  B: E.BUTTERFLY + CDIR_UP,
  x: E.EXPANDING_WALL,
  s: E.SLIME,
  '1': E.KEY,
  '2': E.KEY + 1,
  '3': E.KEY + 2,
  '4': E.DOOR,
  '5': E.DOOR + 1,
  '6': E.DOOR + 2,
  t: E.CLOCK,
};

/** Characters a map may use: the legend plus `?` (random fill). */
export const MAP_CHARS = new Set([...Object.keys(LEGEND), '?']);

/** Legend characters only the Deep set may use. */
export const DEEP_CHARS = new Set(['x', 's', '1', '2', '3', '4', '5', '6', 't']);

/**
 * An element as its legend character, for tests and debugging. States with no character of their
 * own show as the nearest one: a falling boulder as `r`, any firefly as `f` or `F`, explosions as `*`,
 * Digby as `@`, an open exit as `O`, a door opening as `_`.
 */
export function charOf(el: number): string {
  switch (el) {
    case E.BOULDER_FALLING: return 'r';
    case E.DIAMOND_FALLING: return 'd';
    case E.DIGBY: return '@';
    case E.OUTBOX_OPEN: return 'O';
    case E.DOOR_OPENING: return '_';
    default:
  }
  if (isFirefly(el)) return el === E.FIREFLY + CDIR_RIGHT ? 'F' : 'f';
  if (isButterfly(el)) return el === E.BUTTERFLY + CDIR_UP ? 'B' : 'b';
  for (const [ch, id] of Object.entries(LEGEND)) if (id === el) return ch;
  if (el >= E.EXPLODE && el < E.BIRTH + 3) return '*';
  return '#';
}

/** The running cave as rows of legend characters. */
export function dumpCave(s: Pick<CaveState, 'w' | 'h' | 'cells'>): string[] {
  const rows: string[] = [];
  for (let y = 0; y < s.h; y++) {
    let row = '';
    for (let x = 0; x < s.w; x++) row += charOf(s.cells[y * s.w + x] ?? E.STEEL);
    rows.push(row);
  }
  return rows;
}

export { CDIR_DOWN, CDIR_LEFT, CDIR_RIGHT, CDIR_UP };
